import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Save, FileDown, Mail, StickyNote, Search } from "lucide-react";
import { toast } from "sonner";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { computeDocTotals, lineTotalWithVat, fmtPrice } from "@/lib/totals";
import { normalizeForSearch, matchesAllTokens } from "@/lib/searchNormalize";
import InventorySearchDialog from "@/components/billing/InventorySearchDialog";
import Box from "./Box";
import QuoteLinkPanel from "./QuoteLinkPanel";
import { EmailPreviewBlock } from "@/components/email/EmailI18n";

const MAX_ITEMS = 10;

function emptyLine() {
  return {
    _uid: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    product_id: null,
    sku: "",
    name: "",
    description: "",
    kind: "product",
    quantity: 1,
    unit_price: 0,
    vat_rate: 21,
    warranty_months: 0,
  };
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold border-t border-[#111110] pt-1 mt-1" : ""}`}>
      <span className="text-[#525252]">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function SubBox({ label, children }) {
  return (
    <div className="border border-[#E5E5E0] bg-[#FAFAF7] p-1.5">
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252] mb-1 text-center">
        {label}
      </div>
      {children}
    </div>
  );
}

function Dialog({ children, onClose, title }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]">
            <X size={14} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Caja "Productos / servicios" de la pantalla de reparación.
 * Extraída de RepairForm.jsx (iteration_28).
 */
export default function BoxItems({ repair, customer, isNew, repairId, onUpdated, onDirty }) {
  const [items, setItems] = useState([]);
  const [searchOpen, setSearchOpen] = useState(null);
  // Iter 118 — Índice de la línea que abre el buscador avanzado (modal).
  const [advancedForLine, setAdvancedForLine] = useState(null);
  const [parts, setParts] = useState([]);
  const [partsQuery, setPartsQuery] = useState("");
  const [partsById, setPartsById] = useState({});
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendLang, setSendLang] = useState("");
  // Iter 78 — modal "Notas del presupuesto" + draft de la nota en la reparación
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [pricesIncludeVat, setPricesIncludeVat] = useState(true);
  // Opciones globales de garantía (en meses) y default. Se gestionan
  // desde Configuración > Facturación.
  const [warrantyOptions, setWarrantyOptions] = useState([0, 3, 6, 12, 24]);
  const [warrantyDefault, setWarrantyDefault] = useState(3);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        const billing = r.data?.billing || {};
        setPricesIncludeVat(!!(billing.prices_include_vat ?? true));
        if (Array.isArray(billing.warranty_options) && billing.warranty_options.length) {
          setWarrantyOptions(billing.warranty_options);
        }
        if (Number.isFinite(Number(billing.warranty_months_default))) {
          setWarrantyDefault(Number(billing.warranty_months_default));
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    if (repair?.items) {
      setItems(repair.items.length ? repair.items : [emptyLine()]);
    } else if (!repair) {
      setItems([emptyLine()]);
    }
  }, [repair]);

  // Iter 118 — Cargar TODAS las partes 1 sola vez sin filtro backend. El
  // filtrado del dropdown se hace 100% cliente con normalizeForSearch, y el
  // modal avanzado usa el mismo array `parts` completo. Antes hacíamos
  // GET /parts?q=partsQuery en cada tecla, y eso "contaminaba" el modal
  // con el filtro del dropdown.
  useEffect(() => {
    api.get("/parts")
      .then((r) => setParts(r.data || []))
      .catch((err) => console.warn("[BoxItems] /parts falló:", err));
  }, []);

  useEffect(() => {
    api.get("/parts")
      .then((r) => {
        const m = {};
        (r.data || []).forEach((p) => { m[p.id] = p; });
        setPartsById(m);
      })
      .catch((err) => console.warn("[BoxItems] /parts lookup falló:", err));
  }, []);

  const totals = useMemo(() => {
    // Iter 72 — fórmula centralizada en /app/frontend/src/lib/totals.js
    // (espejo de /app/backend/services/totals.py).
    // Iter 168 — recalcular cada línea en vivo (sin usar line_total guardado) para
    // que la vista previa coincida exactamente con el backend (redondeo al alza).
    const live = items.map(({ line_total, ...rest }) => rest);
    const { subtotal, tax, total } = computeDocTotals(live, pricesIncludeVat);
    return { subtotal, tax, total };
  }, [items, pricesIncludeVat]);

  const persist = async (next) => {
    if (isNew || !repairId) return;
    try {
      const r = await api.put(`/repairs/${repairId}`, { items: next });
      onUpdated?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateLine = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    setItems(next);
    onDirty?.();
  };

  const saveLines = async () => {
    if (isNew) {
      toast.info("Guarda primero la reparación antes de añadir líneas");
      return;
    }
    const cleaned = items.filter((it) => (it.name || "").trim() || it.product_id);
    await persist(cleaned);
    toast.success("Líneas guardadas");
  };

  const addLine = () => {
    if (items.length >= MAX_ITEMS) {
      toast.error(`Máximo ${MAX_ITEMS} líneas`);
      return;
    }
    const customerDiscount = Math.max(0, Math.min(100, Number(customer?.discount_pct) || 0));
    setItems([...items, { ...emptyLine(), discount_pct: customerDiscount, warranty_months: warrantyDefault }]);
    onDirty?.();
  };

  const removeLine = (i) => {
    const next = items.length === 1 ? [emptyLine()] : items.filter((_, idx) => idx !== i);
    setItems(next);
    onDirty?.();
  };

  const pickProduct = (i, p) => {
    const currentLine = items[i] || {};
    const customerDiscount = Math.max(0, Math.min(100, Number(customer?.discount_pct) || 0));
    const existingDp = Number(currentLine.discount_pct) || 0;
    const discount_pct = existingDp > 0 ? existingDp : customerDiscount;
    updateLine(i, {
      product_id: p.id,
      sku: p.sku,
      name: p.name,
      description: p.description || "",
      kind: p.kind || "product",
      unit_price: p.sale_price || 0,
      original_unit_price: p.sale_price || 0,
      discount_pct,
      vat_rate: 21,
      // Iter 116 — La garantía se HEREDA exactamente del inventario. Sin
      // fallback silencioso al default global: si el ítem tiene 0 meses,
      // la línea también refleja 0 (el usuario puede editarla manualmente
      // desde el select de la línea si necesita cambiarla en este documento).
      warranty_months: Number(p.warranty_months) || 0,
    });
    setSearchOpen(null);
    setPartsQuery("");
  };

  const downloadPdf = async () => {
    if (isNew || !repairId) return;
    try {
      const url = `${api.defaults.baseURL}/repairs/${repairId}/pdf`;
      const r = await fetch(url, fetchAuthOpts());
      if (!r.ok) {
        toast.error("Error al generar el PDF");
        return;
      }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `reparacion-${repair?.code || "doc"}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn("[BoxItems] downloadPdf falló:", err);
      toast.error("Error al descargar el PDF");
    }
  };

  const openSendDialog = () => {
    if (isNew) {
      toast.info("Guarda primero la reparación");
      return;
    }
    setSendEmail(customer?.email || "");
    setSendLang("");
    setShowSendDialog(true);
  };

  const sendByEmail = async () => {
    if (!sendEmail) {
      toast.error("Indica un email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/repairs/${repairId}/send-pdf`, { to: sendEmail, language_code: sendLang || null });
      toast.success(r.data.message);
      setShowSendDialog(false);
      // Iter 110 — Refresca la reparación en el padre para reflejar el nuevo
      // estimate_status ("sent") y pintar el botón "Situación" en amarillo.
      try {
        const rr = await api.get(`/repairs/${repairId}`);
        onUpdated?.(rr.data);
      } catch (_e) { /* noop */ }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box title="Productos / servicios" testid="repair-box-items">
      <div className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#525252]">
          {items.length} de {MAX_ITEMS} líneas
        </div>
        {items.map((it, i) => (
          <div
            key={it._uid || it.id || `${it.product_id || "new"}-${i}`}
            className="border border-[#E5E5E0] p-2 space-y-1 relative"
            data-testid={`repair-item-${i}`}
          >
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                {searchOpen === i ? (
                  <div className="border border-[#6E8B3D] bg-[#F0F4E8] p-2 space-y-1">
                    <input
                      autoFocus
                      value={partsQuery}
                      onChange={(e) => setPartsQuery(e.target.value)}
                      placeholder="Buscar producto/servicio del inventario..."
                      data-testid={`repair-item-search-${i}`}
                      className="w-full ds-card outline-none px-2 py-1 text-xs rounded-none"
                    />
                    <div className="max-h-40 overflow-y-auto">
                      {(() => {
                        // Iter 125 — Búsqueda por tokens (AND, orden libre).
                        const q = normalizeForSearch(partsQuery);
                        const filtered = q
                          ? parts.filter((p) =>
                              matchesAllTokens(
                                [p.name, p.sku, p.barcode, p.category],
                                partsQuery,
                              ),
                            )
                          : parts;
                        if (filtered.length === 0) {
                          return (
                            <div className="text-[10px] text-[#525252] p-2">
                              {q ? "Sin coincidencias. Prueba con menos texto o usa Buscar." : "Sin resultados. Añade primero desde Inventario."}
                            </div>
                          );
                        }
                        return filtered.slice(0, 8).map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => pickProduct(i, p)}
                            data-testid={`repair-item-pick-${p.id}`}
                            className="w-full text-left px-2 py-1 hover:bg-white text-xs border-b border-[#E5E5E0] last:border-0"
                          >
                            <div className="font-semibold">{p.name}</div>
                            <div className="font-mono text-[10px] text-[#525252]">
                              {p.sku} · {p.kind === "service" ? "Servicio" : "Producto"} · €{p.sale_price?.toFixed(2)}
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                    {/* Iter 118 — Pie: Cerrar (izq) + Buscar avanzado (der). */}
                    <div className="flex items-center justify-between pt-1 border-t border-[#E5E5E0]">
                      <button
                        type="button"
                        onClick={() => setSearchOpen(null)}
                        className="text-[10px] text-[#525252] hover:text-[#111110]"
                        data-testid={`repair-item-search-close-${i}`}
                      >
                        Cerrar
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSearchOpen(null); setAdvancedForLine(i); }}
                        className="text-[10px] inline-flex items-center gap-1 text-[#111110] hover:underline font-semibold"
                        data-testid={`repair-item-search-advanced-${i}`}
                        title="Abrir búsqueda avanzada del inventario"
                      >
                        <Search size={11} /> Buscar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSearchOpen(i)}
                    data-testid={`repair-item-open-${i}`}
                    className="w-full text-left px-2 py-1 border border-[#E5E5E0] hover:border-[#111110] bg-white text-sm"
                  >
                    {it.name ? (
                      <div className="min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                          <span className="truncate">{it.name}</span>
                          {it.product_id && partsById[it.product_id] && (() => {
                            const p = partsById[it.product_id];
                            // Iter 86 — Los servicios no controlan stock: no mostrar badge.
                            if ((p.kind || "product") === "service") return null;
                            const stock = Number(p.stock || 0);
                            const qty = Number(it.quantity || 0);
                            const consumed = it.stock_consumed;
                            if (consumed) {
                              return (
                                <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#F0F4E8] text-[#556C2E] border border-[#6E8B3D] shrink-0">
                                  ✓ Descontado
                                </span>
                              );
                            }
                            const insufficient = stock < qty;
                            return (
                              <span className={`font-mono text-[9px] px-1.5 py-0.5 shrink-0 border ${insufficient ? "bg-red-50 text-red-700 border-red-300" : "bg-[#F7F7F4] text-[#525252] border-[#E5E5E0]"}`}>
                                {insufficient ? "⚠ " : ""}Stock: {stock}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="font-mono text-[10px] text-[#525252] flex items-center gap-2 flex-wrap">
                          <span>{it.sku || "—"}</span>
                          {it.source === "client_addon" && (
                            it.addon_status === "reviewed" ? (
                              <span className="px-1.5 py-0.5 bg-[#F0F4E8] text-[#3F5222] border border-[#6E8B3D] uppercase tracking-widest text-[9px]" data-testid={`addon-line-reviewed-${it.addon_rule_id}`}>
                                ✓ Add-on cliente · Revisado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 bg-[#FFF7E0] text-[#8A6100] border border-[#E8C86A] uppercase tracking-widest text-[9px]" data-testid={`addon-line-pending-${it.addon_rule_id}`}>
                                Aceptado por cliente
                                {!isNew && repairId && (
                                  <button type="button" className="underline normal-case tracking-normal font-sans hover:text-[#111110]"
                                    data-testid={`addon-line-review-btn-${it.addon_rule_id}`}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await api.patch(`/repairs/${repairId}/addon-items/${it.addon_rule_id}/review`);
                                        const r = await api.get(`/repairs/${repairId}`);
                                        onUpdated?.(r.data);
                                        toast.success("Add-on marcado como revisado");
                                      } catch (err) { toast.error(formatApiError(err)); }
                                    }}>
                                    Marcar revisado
                                  </button>
                                )}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-[#525252] text-xs">+ Buscar producto / servicio</div>
                    )}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeLine(i)}
                className="p-1 text-[#525252] hover:text-red-700"
                title="Eliminar línea"
              >
                <X size={12} />
              </button>
            </div>
            {it.name && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-xs">
                <SubBox label="Cantidad">
                  <input
                    type="number"
                    value={Math.max(1, Math.round(Number(it.quantity) || 1))}
                    step="1"
                    min="1"
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateLine(i, { quantity: Number.isFinite(n) && n > 0 ? n : 1 });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "," || e.key === "e") {
                        e.preventDefault();
                      }
                    }}
                    data-testid={`repair-item-qty-${i}`}
                    className="w-full ds-card outline-none px-2 py-1 rounded-none font-mono text-right text-sm"
                  />
                </SubBox>
                <SubBox label="Precio">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={Number(((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2))}
                    onChange={(e) => {
                      const total = Number(e.target.value) || 0;
                      const qty = Number(it.quantity) || 0;
                      const unit = qty > 0 ? total / qty : total;
                      updateLine(i, { unit_price: Number(unit.toFixed(4)), vat_rate: 21 });
                    }}
                    data-testid={`repair-item-total-${i}`}
                    className="w-full ds-card outline-none px-2 py-1 rounded-none font-mono text-right text-sm"
                  />
                  {(() => {
                    const finalLine = lineTotalWithVat(it.quantity, it.unit_price, it.discount_pct);
                    const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                    return ((Number(it.discount_pct) || 0) > 0 || Math.abs(finalLine - gross) > 0.001) && (
                      <div
                        className="text-[10px] font-mono font-bold text-[#6E8B3D] mt-0.5 text-right"
                        data-testid={`repair-item-final-${i}`}
                      >
                        Final: {fmtPrice(finalLine)}
                      </div>
                    );
                  })()}
                </SubBox>
                <SubBox label="Descuento %">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={Number(it.discount_pct) || 0}
                    onChange={(e) => {
                      let n = Number(e.target.value) || 0;
                      if (n < 0) n = 0;
                      if (n > 100) n = 100;
                      updateLine(i, { discount_pct: n });
                    }}
                    data-testid={`repair-item-discount-${i}`}
                    className="w-full ds-card outline-none px-2 py-1 rounded-none font-mono text-right text-sm"
                  />
                  {(Number(it.discount_pct) || 0) > 0 && (
                    <div className="text-[9px] font-mono text-[#6E8B3D] mt-0.5 text-right">
                      −{fmtPrice(((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)) - lineTotalWithVat(it.quantity, it.unit_price, it.discount_pct))}
                    </div>
                  )}
                </SubBox>
                <SubBox label="Garantía">
                  <select
                    value={Number(it.warranty_months) || 0}
                    onChange={(e) => updateLine(i, { warranty_months: parseInt(e.target.value, 10) || 0 })}
                    data-testid={`repair-item-warranty-${i}`}
                    className="w-full ds-card outline-none px-2 py-1 rounded-none font-mono text-sm"
                  >
                    {(() => {
                      // Aseguramos que el valor actual aparece en el desplegable
                      // aunque no esté en las opciones globales (compatibilidad
                      // con documentos históricos con valores legacy).
                      const cur = Number(it.warranty_months) || 0;
                      const opts = warrantyOptions.includes(cur)
                        ? warrantyOptions
                        : [...warrantyOptions, cur].sort((a, b) => a - b);
                      return opts.map((m) => (
                        <option key={m} value={m}>
                          {m === 0 ? "Sin garantía" : m === 1 ? "1 mes" : `${m} meses`}
                        </option>
                      ));
                    })()}
                  </select>
                </SubBox>
              </div>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addLine}
          disabled={items.length >= MAX_ITEMS}
          data-testid="repair-item-add"
          className="w-full border border-dashed border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs text-[#525252] disabled:opacity-50 inline-flex items-center justify-center gap-1"
        >
          <Plus size={12} /> Añadir línea
        </button>
        <div className="pt-2 border-t border-[#E5E5E0] space-y-0.5 font-mono text-xs">
          <Row label="Base imponible" value={`${totals.subtotal.toFixed(2).replace(".", ",")} €`} />
          <Row label="IVA" value={`${totals.tax.toFixed(2).replace(".", ",")} €`} />
          <Row label={pricesIncludeVat ? "TOTAL (IVA incluido)" : "TOTAL"} value={fmtPrice(totals.total)} bold />
        </div>
        <div className="pt-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={saveLines}
            disabled={isNew}
            data-testid="repair-items-save"
            className="inline-flex items-center gap-1 bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            <Save size={12} /> Guardar líneas
          </button>
          <button
            type="button"
            onClick={openSendDialog}
            disabled={isNew}
            data-testid="repair-items-pdf"
            className="inline-flex items-center gap-1 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            <FileDown size={12} /> PDF / Enviar
          </button>
          <button
            type="button"
            onClick={() => {
              setNotesDraft(repair?.quote_notes ?? repair?.observations ?? "");
              setShowNotesDialog(true);
            }}
            disabled={isNew}
            data-testid="repair-items-notes"
            title="Nota libre del presupuesto"
            className="inline-flex items-center gap-1 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            <StickyNote size={12} /> Nota
          </button>
        </div>
        {isNew && (
          <div className="text-[10px] text-amber-700">
            Guarda la reparación primero para añadir líneas y generar PDFs.
          </div>
        )}
      </div>

      {showSendDialog && (
        <Dialog onClose={() => setShowSendDialog(false)} title="PDF de productos / servicios">
          <p className="text-xs text-[#525252] mb-3">
            ¿Qué quieres hacer con el documento?
          </p>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { downloadPdf(); setShowSendDialog(false); }}
              data-testid="repair-pdf-download"
              className="w-full inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm font-semibold"
            >
              <FileDown size={14} /> Descargar PDF
            </button>
            <div className="pt-2 border-t border-[#E5E5E0]">
              {!(customer?.email || "").trim() ? (
                <div className="space-y-2">
                  <div
                    className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    data-testid="repair-pdf-no-email-warning"
                  >
                    Este cliente no tiene una dirección de email configurada.
                  </div>
                  {customer?.id && (
                    <Link
                      to={`/customers/${customer.id}`}
                      onClick={() => setShowSendDialog(false)}
                      data-testid="repair-pdf-goto-customer"
                      className="block text-center bg-[#111110] hover:bg-black text-white px-3 py-2 text-sm font-semibold"
                    >
                      Ir a ficha de cliente →
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                    Enviar por email a: (varios separados por , o ;)
                  </label>
                  <input
                    type="text"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    placeholder="email@cliente.com, otro@empresa.com"
                    data-testid="repair-pdf-email"
                    className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
                  />
                  <p className="mt-1 text-[10px] text-[#525252]">
                    Rellenado automáticamente desde la ficha del cliente. Puedes editarlo antes de enviar.
                  </p>
                  <div className="mt-3">
                    <EmailPreviewBlock templateKey="quote_send" repairId={repairId} language={sendLang} onLanguage={(v) => setSendLang(v)} />
                  </div>
                  <button
                    type="button"
                    onClick={sendByEmail}
                    disabled={sending}
                    data-testid="repair-pdf-send"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
                  >
                    <Mail size={14} /> {sending ? "Enviando..." : "Enviar por mail"}
                  </button>
                </>
              )}
            </div>
            <QuoteLinkPanel repairId={repairId} customer={customer} onUpdated={onUpdated} />
          </div>
        </Dialog>
      )}

      {showNotesDialog && (
        <Dialog onClose={() => setShowNotesDialog(false)} title="Notas del presupuesto">
          <p className="text-xs text-[#525252] mb-2">
            Texto libre que aparecerá como <b>NOTAS</b> en el PDF del presupuesto.
            Admite saltos de línea.
          </p>
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            data-testid="repair-notes-textarea"
            rows={10}
            autoFocus
            placeholder="Escribe aquí la nota del presupuesto…"
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono leading-relaxed"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowNotesDialog(false)}
              data-testid="repair-notes-cancel"
              className="px-4 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0] hover:bg-[#F7F7F4]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!repairId) return;
                setSavingNotes(true);
                try {
                  const r = await api.put(`/repairs/${repairId}`, { quote_notes: notesDraft });
                  toast.success("Nota guardada");
                  onUpdated?.(r.data);
                  setShowNotesDialog(false);
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setSavingNotes(false);
                }
              }}
              disabled={savingNotes}
              data-testid="repair-notes-save"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-[#6E8B3D] hover:bg-[#556C2E] text-white font-semibold disabled:opacity-50"
            >
              <Save size={12} /> {savingNotes ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </Dialog>
      )}
      <InventorySearchDialog
        open={advancedForLine !== null}
        onClose={() => setAdvancedForLine(null)}
        parts={parts}
        onAdd={(picked) => {
          // Iter 118 — Añadimos el(los) artículo(s) seleccionado(s) a la línea
          // en curso. Si se selecciona uno solo, se rellena la línea `i`. Si
          // son varios, se rellena `i` con el primero y se añaden nuevas
          // líneas para el resto (respetando MAX_ITEMS).
          const idx = advancedForLine;
          if (idx === null || !picked || picked.length === 0) return;
          const first = picked[0];
          const p = parts.find((x) => x.id === first.product_id);
          if (p) pickProduct(idx, p);
          if (picked.length > 1) {
            const extras = picked.slice(1).map((pk) => {
              const src = parts.find((x) => x.id === pk.product_id);
              if (!src) return null;
              return {
                _uid: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                product_id: src.id,
                sku: src.sku || "",
                name: src.name || "",
                description: src.description || "",
                kind: src.kind || "product",
                quantity: pk.quantity || 1,
                unit_price: Number(src.sale_price) || 0,
                vat_rate: src.vat_rate ?? 21,
                warranty_months: Number(src.warranty_months) || 0,
              };
            }).filter(Boolean);
            if (extras.length > 0) {
              const room = MAX_ITEMS - items.length;
              const toAdd = extras.slice(0, Math.max(0, room));
              if (toAdd.length > 0) {
                setItems([...items, ...toAdd]);
                onDirty && onDirty(true);
              }
              if (extras.length > toAdd.length) {
                toast.warning(`Se han añadido ${toAdd.length} de ${extras.length} artículos (límite ${MAX_ITEMS}).`);
              }
            }
          }
          setAdvancedForLine(null);
        }}
      />
    </Box>
  );
}
